import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Timeline } from 'primereact/timeline';
import { Badge } from 'primereact/badge';
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import { FilterMatchMode } from 'primereact/api';
import LoanService from '../services/LoanService';
import LoanApplicationService from '../services/LoanApplicationService';
import SmartDocumentChecklist from '../components/documents/SmartDocumentChecklist';
import axiosInstance from '../config/axios.config';

// Helper functions
const getStatusSeverity = (status) => {
    const severityMap = {
        'draft': 'secondary',
        'submitted': 'info',
        'under_review': 'warning',
        'approved': 'success',
        'rejected': 'danger',
        'expired': 'danger'
    };
    return severityMap[status] || 'info';
};

const getRiskSeverity = (riskCategory) => {
    const severityMap = {
        'low': 'success',
        'medium': 'warning', 
        'high': 'danger',
        'very_high': 'danger'
    };
    return severityMap[riskCategory] || 'info';
};

const LoanApplications = () => {
    const [applications, setApplications] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [applicationDialog, setApplicationDialog] = useState(false);
    const [applicationDetailDialog, setApplicationDetailDialog] = useState(false);
    const [newApplicationDialog, setNewApplicationDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statistics, setStatistics] = useState(null);
    const [loanProducts, setLoanProducts] = useState([]);
    const [newApplication, setNewApplication] = useState({
        loan_product_id: '',
        requested_amount: 100000,
        requested_tenure_months: 24,
        application_source: 'online',
        priority: 'medium',
        customer_data: {
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            pan: '',
        }
    });
    
    const toast = useRef(null);
    const dt = useRef(null);

    const [filters, setFilters] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'application_number': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'customer_name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'status': { value: null, matchMode: FilterMatchMode.EQUALS },
        'stage': { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    const statusOptions = [
        { label: 'Draft', value: 'draft' },
        { label: 'Submitted', value: 'submitted' },
        { label: 'Under Review', value: 'under_review' },
        { label: 'Documents Pending', value: 'documents_pending' },
        { label: 'Credit Assessment', value: 'credit_assessment' },
        { label: 'Lender Review', value: 'lender_review' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
    ];

    useEffect(() => {
        loadApplications();
        loadStatistics();
        loadLoanProducts();
    }, []);

    const loadApplications = async () => {
        try {
            setLoading(true);
            const response = await LoanApplicationService.getLoanApplications();
            if (response.success) {
                setApplications(response.data.data || response.data);
            }
        } catch (error) {
            console.error('Error loading applications:', error);
            console.log('Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message
            });
            
            // Use empty array as fallback
            setApplications([]);
            
            if (error.response?.status === 401) {
                toast.current.show({ 
                    severity: 'warn', 
                    summary: 'Authentication Required', 
                    detail: 'Please ensure you are logged in to view loan applications',
                    life: 3000 
                });
            } else if (error.response?.status === 500) {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Server Error', 
                    detail: 'There was a server error. The loan applications feature may need authentication.',
                    life: 4000 
                });
            } else {
                toast.current.show({ 
                    severity: 'info', 
                    summary: 'Info', 
                    detail: 'Loan applications will be available once you are authenticated.',
                    life: 3000 
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            const response = await LoanApplicationService.getStatistics();
            if (response.success) {
                setStatistics(response.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
            // Use fallback data
            setStatistics({
                total_applications: 0,
                pending_action: 0,
                expiring_soon: 0,
                assigned_to_me: 0,
                by_status: {},
                by_stage: {}
            });
            
            if (error.response?.status !== 401) {
                toast.current.show({ 
                    severity: 'warn', 
                    summary: 'Warning', 
                    detail: 'Could not load statistics. Using fallback data.',
                    life: 2000 
                });
            }
        }
    };

    const loadLoanProducts = async () => {
        try {
            const response = await LoanService.getActiveLoanProducts();
            if (response.success) {
                const productOptions = response.data.map(product => ({
                    label: `${product.product_name} (${product.base_rate}%)`,
                    value: product.id
                }));
                setLoanProducts(productOptions);
            }
        } catch (error) {
            console.error('Error loading loan products:', error);
        }
    };

    const openNewApplication = () => {
        setNewApplication({
            loan_product_id: '',
            requested_amount: 100000,
            requested_tenure_months: 24,
            application_source: 'online',
            priority: 'medium',
            customer_data: {
                first_name: '',
                last_name: '',
                phone: '',
                email: '',
                pan: '',
            }
        });
        setNewApplicationDialog(true);
    };

    const viewApplicationDetails = (application) => {
        setSelectedApplication(application);
        setApplicationDetailDialog(true);
    };

    const editApplication = (application) => {
        setSelectedApplication(application);
        setApplicationDialog(true);
    };

    const submitApplication = async (application) => {
        try {
            const response = await LoanApplicationService.submitApplication(application.id);
            const data = response;
            
            if (data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Application submitted successfully', 
                    life: 3000 
                });
                loadApplications();
            } else {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: data.message, 
                    life: 5000 
                });
            }
        } catch (error) {
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Failed to submit application', 
                life: 5000 
            });
        }
    };

    const statusBodyTemplate = (rowData) => {
        const severity = getStatusSeverity(rowData.status);
        return <Tag value={rowData.status_display_name} severity={severity} />;
    };

    const stageBodyTemplate = (rowData) => {
        return <Chip label={rowData.stage_display_name} className="text-sm" />;
    };

    const amountBodyTemplate = (rowData) => {
        return `₹${rowData.requested_amount?.toLocaleString()}`;
    };

    const progressBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <ProgressBar value={rowData.completeness_percentage} style={{ width: '100px', height: '6px' }}  aria-label="Progress" />
                <span className="text-sm">{Math.round(rowData.completeness_percentage)}%</span>
            </div>
        );
    };

    const urgentBodyTemplate = (rowData) => {
        return rowData.requires_urgent_attention ? 
            <Badge value="Urgent" severity="danger" /> : null;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button 
                    icon="pi pi-eye" 
                    className="p-button-rounded p-button-info p-button-text" 
                    onClick={() => viewApplicationDetails(rowData)}
                    tooltip="View Details"
                />
                {rowData.is_editable && (
                    <Button 
                        icon="pi pi-pencil" 
                        className="p-button-rounded p-button-success p-button-text" 
                        onClick={() => editApplication(rowData)}
                        tooltip="Edit"
                    />
                )}
                {rowData.can_be_submitted && (
                    <Button 
                        icon="pi pi-send" 
                        className="p-button-rounded p-button-warning p-button-text" 
                        onClick={() => submitApplication(rowData)}
                        tooltip="Submit"
                    />
                )}
            </div>
        );
    };

    const createNewApplication = async () => {
        try {
            // Validate required fields
            if (!newApplication.loan_product_id || 
                !newApplication.requested_amount || 
                !newApplication.requested_tenure_months ||
                !newApplication.customer_data?.first_name ||
                !newApplication.customer_data?.phone) {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Validation Error', 
                    detail: 'Please fill in all required fields',
                    life: 3000 
                });
                return;
            }

            console.log('Creating application with data:', newApplication);
            const response = await LoanApplicationService.createLoanApplication(newApplication);
            
            if (response.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Loan application created successfully', 
                    life: 3000 
                });
                setNewApplicationDialog(false);
                loadApplications();
                
                // Reset form
                setNewApplication({
                    loan_product_id: '',
                    requested_amount: 100000,
                    requested_tenure_months: 24,
                    application_source: 'online',
                    priority: 'medium',
                    customer_data: {
                        first_name: '',
                        last_name: '',
                        phone: '',
                        email: '',
                        pan: '',
                    }
                });
            }
        } catch (error) {
            console.error('Error creating application:', error);
            console.log('Error details:', {
                status: error.response?.status,
                data: error.response?.data,
                validationErrors: error.response?.data?.errors
            });
            
            let errorMessage = 'Failed to create loan application';
            
            if (error.response?.status === 400) {
                // Validation errors
                const validationErrors = error.response?.data?.errors;
                if (validationErrors) {
                    const errorMessages = Object.values(validationErrors).flat();
                    errorMessage = 'Validation errors: ' + errorMessages.join(', ');
                } else {
                    errorMessage = error.response?.data?.message || 'Invalid input data';
                }
            } else if (error.response?.status === 401) {
                errorMessage = 'Please log in to create loan applications';
            } else {
                errorMessage = error.response?.data?.message || 'Failed to create loan application';
            }
            
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: errorMessage, 
                life: 5000 
            });
        }
    };

    const getStatusSeverity = (status) => {
        const severityMap = {
            'draft': 'secondary',
            'submitted': 'info',
            'under_review': 'warning',
            'approved': 'success',
            'rejected': 'danger',
            'expired': 'danger'
        };
        return severityMap[status] || 'info';
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex align-items-center gap-2">
                <Button label="New Application" icon="pi pi-plus" className="p-button-success" onClick={openNewApplication} />
                <Button label="Refresh" icon="pi pi-refresh" className="p-button-help" onClick={loadApplications} />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between">
            <h5 className="m-0">Loan Applications</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." />
            </span>
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    
                    {/* Statistics Cards */}
                    {statistics && (
                        <div className="grid mb-4">
                            <div className="col-12 lg:col-3">
                                <Card className="bg-blue-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Total Applications</span>
                                            <div className="text-900 font-medium text-xl">{statistics.total_applications}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-blue-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-file-edit text-blue-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-orange-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Pending Action</span>
                                            <div className="text-900 font-medium text-xl">{statistics.pending_action}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-orange-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-clock text-orange-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-red-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Expiring Soon</span>
                                            <div className="text-900 font-medium text-xl">{statistics.expiring_soon}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-red-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-exclamation-triangle text-red-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-green-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">My Applications</span>
                                            <div className="text-900 font-medium text-xl">{statistics.assigned_to_me}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-green-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-user text-green-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    )}

                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={applications}
                        dataKey="id"
                        paginator
                        rows={15}
                        rowsPerPageOptions={[10, 15, 25]}
                        className="p-datatable-gridlines"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} applications"
                        globalFilter={globalFilter}
                        filters={filters}
                        filterDisplay="menu"
                        loading={loading}
                        responsiveLayout="scroll"
                        header={header}
                    >
                        <Column field="application_number" header="Application #" sortable style={{ minWidth: '12rem' }} />
                        <Column field="customer_name" header="Customer" sortable style={{ minWidth: '12rem' }} />
                        <Column field="requested_amount" header="Amount" body={amountBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                        <Column field="stage" header="Stage" body={stageBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                        <Column header="Progress" body={progressBodyTemplate} style={{ minWidth: '12rem' }} />
                        <Column header="Urgent" body={urgentBodyTemplate} style={{ minWidth: '6rem' }} />
                        <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '12rem' }}></Column>
                    </DataTable>

                    {/* New Application Dialog */}
                    <Dialog 
                        visible={newApplicationDialog} 
                        style={{ width: '800px' }} 
                        header="Create New Loan Application" 
                        modal 
                        className="p-fluid"
                        onHide={() => setNewApplicationDialog(false)}
                        footer={
                            <div>
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text" 
                                    onClick={() => setNewApplicationDialog(false)} />
                                <Button label="Create Application" icon="pi pi-check" className="p-button-text" 
                                    onClick={createNewApplication} />
                            </div>
                        }
                    >
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="loan_product_id">Loan Product *</label>
                                    <Dropdown 
                                        id="loan_product_id"
                                        value={newApplication.loan_product_id} 
                                        options={loanProducts} 
                                        onChange={(e) => setNewApplication({...newApplication, loan_product_id: e.value})} 
                                        placeholder="Select Loan Product"
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="requested_amount">Requested Amount (₹) *</label>
                                    <InputNumber 
                                        id="requested_amount" 
                                        value={newApplication.requested_amount} 
                                        onValueChange={(e) => setNewApplication({...newApplication, requested_amount: e.value})} 
                                        mode="currency" 
                                        currency="INR"
                                        locale="en-IN"
                                        min={1000}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="requested_tenure_months">Tenure (Months) *</label>
                                    <InputNumber 
                                        id="requested_tenure_months" 
                                        value={newApplication.requested_tenure_months} 
                                        onValueChange={(e) => setNewApplication({...newApplication, requested_tenure_months: e.value})} 
                                        min={6}
                                        max={360}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="priority">Priority</label>
                                    <Dropdown 
                                        id="priority"
                                        value={newApplication.priority} 
                                        options={[
                                            { label: 'Low', value: 'low' },
                                            { label: 'Medium', value: 'medium' },
                                            { label: 'High', value: 'high' },
                                            { label: 'Urgent', value: 'urgent' }
                                        ]} 
                                        onChange={(e) => setNewApplication({...newApplication, priority: e.value})} 
                                        placeholder="Select Priority"
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <h6>Customer Details</h6>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="first_name">First Name *</label>
                                    <InputText 
                                        id="first_name" 
                                        value={newApplication.customer_data?.first_name || ''} 
                                        onChange={(e) => setNewApplication({
                                            ...newApplication, 
                                            customer_data: {
                                                ...(newApplication.customer_data || {}),
                                                first_name: e.target.value
                                            }
                                        })} 
                                        placeholder="Enter first name"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="last_name">Last Name</label>
                                    <InputText 
                                        id="last_name" 
                                        value={newApplication.customer_data?.last_name || ''} 
                                        onChange={(e) => setNewApplication({
                                            ...newApplication, 
                                            customer_data: {
                                                ...(newApplication.customer_data || {}),
                                                last_name: e.target.value
                                            }
                                        })} 
                                        placeholder="Enter last name"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="phone">Phone Number *</label>
                                    <InputText 
                                        id="phone" 
                                        value={newApplication.customer_data?.phone || ''} 
                                        onChange={(e) => setNewApplication({
                                            ...newApplication, 
                                            customer_data: {
                                                ...(newApplication.customer_data || {}),
                                                phone: e.target.value
                                            }
                                        })} 
                                        placeholder="Enter phone number"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="email">Email</label>
                                    <InputText 
                                        id="email" 
                                        value={newApplication.customer_data?.email || ''} 
                                        onChange={(e) => setNewApplication({
                                            ...newApplication, 
                                            customer_data: {
                                                ...(newApplication.customer_data || {}),
                                                email: e.target.value
                                            }
                                        })} 
                                        placeholder="Enter email"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="pan">PAN Number</label>
                                    <InputText 
                                        id="pan" 
                                        value={newApplication.customer_data?.pan || ''} 
                                        onChange={(e) => setNewApplication({
                                            ...newApplication, 
                                            customer_data: {
                                                ...(newApplication.customer_data || {}),
                                                pan: e.target.value.toUpperCase()
                                            }
                                        })} 
                                        placeholder="Enter PAN number"
                                        maxLength={10}
                                    />
                                </div>
                            </div>
                        </div>
                    </Dialog>

                    {/* Application Detail Dialog */}
                    <Dialog 
                        visible={applicationDetailDialog} 
                        style={{ width: '1200px' }} 
                        header={selectedApplication ? `Application ${selectedApplication.application_number}` : ''} 
                        modal 
                        onHide={() => setApplicationDetailDialog(false)}
                        maximizable
                    >
                        {selectedApplication && (
                            <TabView>
                                <TabPanel header="Overview">
                                    <ApplicationOverview application={selectedApplication} />
                                </TabPanel>
                                <TabPanel header="Documents">
                                    <SmartDocumentChecklist applicationId={selectedApplication.id} />
                                </TabPanel>
                                <TabPanel header="Credit Assessment">
                                    <CreditAssessmentView application={selectedApplication} />
                                </TabPanel>
                                <TabPanel header="Workflow History">
                                    <WorkflowHistory application={selectedApplication} />
                                </TabPanel>
                            </TabView>
                        )}
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

// Supporting Components
const ApplicationOverview = ({ application }) => (
    <div className="grid">
        <div className="col-12 md:col-6">
            <Card title="Application Details">
                <div className="field">
                    <label>Application Number:</label>
                    <div className="font-bold">{application.application_number}</div>
                </div>
                <div className="field">
                    <label>Customer:</label>
                    <div className="font-bold">{application.customer_name}</div>
                </div>
                <div className="field">
                    <label>Product:</label>
                    <div className="font-bold">{application.loan_product?.product_name}</div>
                </div>
                <div className="field">
                    <label>Requested Amount:</label>
                    <div className="font-bold text-2xl text-blue-500">₹{application.requested_amount?.toLocaleString()}</div>
                </div>
                <div className="field">
                    <label>Tenure:</label>
                    <div className="font-bold">{application.requested_tenure_months} months</div>
                </div>
                <div className="field">
                    <label>Estimated EMI:</label>
                    <div className="font-bold text-green-500">₹{application.estimated_emi?.toLocaleString()}</div>
                </div>
            </Card>
        </div>
        <div className="col-12 md:col-6">
            <Card title="Status & Progress">
                <div className="field">
                    <label>Current Status:</label>
                    <Tag value={application.status_display_name} severity={getStatusSeverity(application.status)} />
                </div>
                <div className="field">
                    <label>Current Stage:</label>
                    <Chip label={application.stage_display_name} />
                </div>
                <div className="field">
                    <label>Completion:</label>
                    <ProgressBar value={application.completeness_percentage}  aria-label="Progress" />
                    <small>{Math.round(application.completeness_percentage)}% Complete</small>
                </div>
                <div className="field">
                    <label>Next Action:</label>
                    <div className="font-semibold text-orange-500">{application.next_action || 'No action required'}</div>
                </div>
                {application.requires_urgent_attention && (
                    <div className="field">
                        <Badge value="Requires Urgent Attention" severity="danger" />
                    </div>
                )}
            </Card>
        </div>
    </div>
);

const ApplicationDocuments = ({ application }) => (
    <DataTable value={application.documents || []} className="p-datatable-sm">
        <Column field="document_name" header="Document" />
        <Column field="document_type" header="Type" />
        <Column field="verification_status" header="Status" body={(rowData) => 
            <Tag value={rowData.verification_status} severity={rowData.is_verified ? 'success' : 'warning'} />
        } />
        <Column field="upload_date" header="Uploaded" />
        <Column body={(rowData) => rowData.is_verified && (
            <Button icon="pi pi-download" aria-label="Download" className="p-button-text" tooltip="Download" />
        )} />
    </DataTable>
);

const CreditAssessmentView = ({ application }) => {
    const assessment = application.credit_assessment;
    
    if (!assessment) {
        return <div className="text-center p-4 text-500">No credit assessment available</div>;
    }

    return (
        <div className="grid">
            <div className="col-12 md:col-4">
                <Card title="Credit Score" className="text-center">
                    <div className="text-6xl font-bold text-blue-500">{assessment.final_credit_score || 'N/A'}</div>
                    <div className="text-500 mt-2">CIBIL Score</div>
                </Card>
            </div>
            <div className="col-12 md:col-4">
                <Card title="Risk Category" className="text-center">
                    <Tag value={assessment.risk_category} severity={getRiskSeverity(assessment.risk_category)} className="text-lg" />
                    <div className="text-500 mt-2">Risk Level</div>
                </Card>
            </div>
            <div className="col-12 md:col-4">
                <Card title="Recommended Amount" className="text-center">
                    <div className="text-2xl font-bold text-green-500">₹{assessment.recommended_amount?.toLocaleString()}</div>
                    <div className="text-500 mt-2">Max Eligible</div>
                </Card>
            </div>
            <div className="col-12">
                <Panel header="Assessment Summary">
                    <p>{assessment.assessment_summary || 'Assessment in progress...'}</p>
                </Panel>
            </div>
        </div>
    );
};

const WorkflowHistory = ({ application }) => {
    const history = application.workflow_history || [];
    
    return (
        <Timeline 
            value={history} 
            content={(item) => (
                <div>
                    <div className="font-bold">{item.action}</div>
                    <div className="text-500">{item.description}</div>
                    <small>{new Date(item.timestamp).toLocaleString()}</small>
                </div>
            )}
        />
    );
};

export default LoanApplications;